import React, { useState, useEffect } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle, Input,
  Label, Button, Spinner, Modal, ModalHeader, ModalBody, Badge, UncontrolledTooltip
} from "reactstrap";

// Import Editor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Toastify for notifications
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AboutUsList = () => {
  document.title = "Hakkımızda Yönetimi | LojGo Yönetim";

  // --- STATE TANIMLAMALARI ---
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal State'leri
  const [modal, setModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form State'i
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    Section_Type: "Story",
    Title: "",
    Description: "",
    Display_Order: 0,
    IsActive: true,
    Icon_Url: ""
  });

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const isAdmin = authUser?.Users_Type_ID === 3;

  // --- API İŞLEMLERİ ---

  // 1. Verileri Çek (GET)
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("https://hiflowapi.stechomeyazilim.info/About_Us?$orderby=Display_Order asc");
      const result = await response.json();
      if (result && result.value) {
        setItems(result.value);
      }
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // 2. Silme İşlemi (DELETE)
  const handleDelete = async (id) => {
    if (window.confirm("Bu içeriği silmek istediğinize emin misiniz?")) {
      try {
        const response = await fetch(`https://hiflowapi.stechomeyazilim.info/About_Us(${id})`, {
          method: "DELETE"
        });
        if (response.ok) {
          toast.success("İçerik başarıyla silindi.");
          fetchItems();
        } else {
          toast.error("Silme işlemi başarısız oldu.");
        }
      } catch (error) {
        toast.error("Hata oluştu.");
      }
    }
  };

  // 3. Kaydetme / Güncelleme (POST / PUT)
  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.Title || !formData.Description) {
      toast.warning("Lütfen başlık ve açıklama alanlarını doldurun.");
      return;
    }

    setIsSaving(true);
    try {
      let finalIconUrl = formData.Icon_Url;

      // Yeni resim seçildiyse önce yükle
      if (selectedFile) {
        const uploadData = new FormData();
        uploadData.append("file", selectedFile);
        const uploadRes = await fetch("https://hiflowapi.stechomeyazilim.info/Uploads/uploads.php", {
          method: "POST",
          body: uploadData
        });
        const uploadResult = await uploadRes.json();
        if (uploadResult.status) {
          finalIconUrl = uploadResult.url;
        } else {
          toast.error("Görsel yüklenirken bir hata oluştu.");
          setIsSaving(false);
          return;
        }
      }

      const payload = {
        Section_Type: formData.Section_Type,
        Title: formData.Title,
        Description: formData.Description,
        Display_Order: Number(formData.Display_Order),
        IsActive: formData.IsActive,
        Icon_Url: finalIconUrl
      };

      const url = editId
        ? `https://hiflowapi.stechomeyazilim.info/About_Us(${editId})`
        : "https://hiflowapi.stechomeyazilim.info/About_Us";

      const method = editId ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editId ? "İçerik güncellendi!" : "Yeni içerik eklendi!");
        toggleModal();
        fetchItems();
      } else {
        toast.error("Kayıt sırasında bir hata oluştu.");
      }
    } catch (error) {
      toast.error("İşlem başarısız. Lütfen bağlantınızı kontrol edin.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- MODAL YÖNETİMİ ---
  
  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      // Modalı kapatırken form verilerini sıfırla
      setEditId(null);
      setFormData({
        Section_Type: "Story", Title: "", Description: "",
        Display_Order: 0, IsActive: true, Icon_Url: ""
      });
      setSelectedFile(null);
      setImagePreview(null);
    }
  };

  const handleAddClick = () => {
    toggleModal(); // Sıfırlanmış formla açar
  };

  const handleEditClick = (item) => {
    setEditId(item.ID);
    setFormData({
      Section_Type: item.Section_Type || "Story",
      Title: item.Title || "",
      Description: item.Description || "",
      Display_Order: item.Display_Order || 0,
      IsActive: item.IsActive,
      Icon_Url: item.Icon_Url || ""
    });
    setImagePreview(item.Icon_Url || null);
    setSelectedFile(null);
    setModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Bölüm tiplerini okunabilir hale getiren yardımcı fonksiyon
  const getSectionBadge = (type) => {
    switch (type) {
      case "Story": return <Badge color="primary">Ana Hikaye</Badge>;
      case "Core_Value": return <Badge color="success">Temel Nitelik</Badge>;
      case "Process_Step": return <Badge color="info">Süreç Adımı</Badge>;
      case "Stat": return <Badge color="dark">İstatistik</Badge>;
      case "KVKK": return <Badge color="primary">KVKK</Badge>;
      case "PrivacyPolicy": return <Badge color="info">Gizlilik Politikası</Badge>;
      case "AdvertTerms": return <Badge color="warning">İlan Şartları</Badge>;
      case "Contract": return <Badge color="success">Sözleşme</Badge>;
      default: return <Badge color="secondary">{type}</Badge>;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Kurumsal" breadcrumbItem="Hakkımızda Yönetimi" />

          {/* ÜST BAŞLIK VE EKLE BUTONU */}
          <Row className="mb-4">
            <Col className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Hakkımızda Sayfası İçerikleri</h4>
              {isAdmin && (
                <Button color="success" className="rounded-pill px-4" onClick={handleAddClick}>
                  <i className="mdi mdi-plus me-1"></i> Yeni İçerik Ekle
                </Button>
              )}
            </Col>
          </Row>

          {/* İÇERİK LİSTESİ */}
          <Row>
            {isLoading ? (
              <Col lg={12} className="text-center py-5">
                <Spinner color="primary" />
              </Col>
            ) : items.length === 0 ? (
              <Col lg={12}>
                <Card><CardBody className="text-center">Henüz içerik eklenmemiş.</CardBody></Card>
              </Col>
            ) : (
              items.map((item) => (
                <Col md={6} xl={4} key={item.ID} className="mb-4">
                  <Card className="h-100 shadow-sm border-0">
                    <CardBody className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          {getSectionBadge(item.Section_Type)}
                          {!item.IsActive && <Badge color="warning">Pasif</Badge>}
                        </div>
                        {isAdmin && (
                          <div className="d-flex gap-1">
                            <Button color="light" size="sm" onClick={() => handleEditClick(item)} id={`edit-${item.ID}`} className="p-1">
                              <i className="mdi mdi-pencil text-success font-size-16"></i>
                            </Button>
                            <Button color="light" size="sm" onClick={() => handleDelete(item.ID)} id={`del-${item.ID}`} className="p-1">
                              <i className="mdi mdi-delete text-danger font-size-16"></i>
                            </Button>
                            <UncontrolledTooltip target={`edit-${item.ID}`}>Düzenle</UncontrolledTooltip>
                            <UncontrolledTooltip target={`del-${item.ID}`}>Sil</UncontrolledTooltip>
                          </div>
                        )}
                      </div>

                      <div className="d-flex gap-3 mb-3">
                        {item.Icon_Url && (
                          <div className="flex-shrink-0">
                            <img src={item.Icon_Url} alt="" className="rounded shadow-sm" style={{ width: "60px", height: "60px", objectFit: "cover" }} />
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <h5 className="font-size-16 fw-bold mb-1">{item.Title}</h5>
                          <small className="text-muted">Sıra: {item.Display_Order}</small>
                        </div>
                      </div>

                      <div 
                        className="text-muted text-truncate" 
                        style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        dangerouslySetInnerHTML={{ __html: item.Description }} 
                      />
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Container>
      </div>

      {/* EKLE / DÜZENLE MODALI */}
      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal} className="bg-light">
          {editId ? "İçeriği Düzenle" : "Yeni İçerik Ekle"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSave}>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Bölüm Türü</Label>
                  <Input
                    type="select"
                    value={formData.Section_Type}
                    onChange={(e) => setFormData({ ...formData, Section_Type: e.target.value })}
                  >
                    <option value="Story">Ana Hikaye (Story)</option>
                    <option value="Core_Value">Temel Nitelik (Maksimum Güven vb.)</option>
                    <option value="Process_Step">Süreç Adımı (01, 02, 03...)</option>
                    <option value="Stat">İstatistik / Rakam (81 Şehir vb.)</option>
                    <option value="KVKK">KVKK Aydınlatma Metni</option>
                    <option value="PrivacyPolicy">Gizlilik ve Çerez Politikası</option>
                    <option value="AdvertTerms">İlan Verme ve Taşıma Hizmet Sözleşmesi</option>
                    <option value="Contract">Platform Kullanım ve Araç Transfer Sözleşmesi</option>
                  </Input>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Başlık</Label>
                  <Input
                    type="text"
                    required
                    placeholder="Örn: Türkiye'nin Her Noktasına..."
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                  />
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label>Açıklama</Label>
              <CKEditor
                editor={ClassicEditor}
                data={formData.Description}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setFormData({ ...formData, Description: data });
                }}
              />
            </div>

            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label>İkon / Görsel (Opsiyonel)</Label>
                  <Input type="file" onChange={handleFileChange} accept="image/*" />
                  {imagePreview && (
                    <div className="mt-2">
                      <img src={imagePreview} alt="Önizleme" className="img-thumbnail" style={{ maxHeight: "80px" }} />
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Sıralama (Display Order)</Label>
                  <Input
                    type="number"
                    value={formData.Display_Order}
                    onChange={(e) => setFormData({ ...formData, Display_Order: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Durum</Label>
                  <Input
                    type="select"
                    value={formData.IsActive}
                    onChange={(e) => setFormData({ ...formData, IsActive: e.target.value === "true" })}
                  >
                    <option value={true}>Aktif (Sitede Görünür)</option>
                    <option value={false}>Pasif (Gizli)</option>
                  </Input>
                </div>
              </Col>
            </Row>

            <div className="text-end mt-3">
              <Button color="light" className="me-2" onClick={toggleModal}>İptal</Button>
              <Button type="submit" color="primary" disabled={isSaving}>
                {isSaving ? <><Spinner size="sm" className="me-2" /> Kaydediliyor...</> : "Kaydet"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer autoClose={2000} position="bottom-right" />
    </React.Fragment>
  );
};

export default AboutUsList;